import { Component, EventEmitter, Inject, Input, OnInit, Output, ViewChild } from "@angular/core";
import { SpinnerService } from "@app/services/spinner.service";
import { AddressFormComponentProps, AddressFormData, CardAddressComponent } from "@app/components/card/card-address.component";
import { take } from "rxjs/operators";
import { PaymentMethodApi } from "@app/services/apiServices/PaymentMethodApi";
import { CardDetailEditComponent, CardDetailEditData, CardDetailEditProps } from "@app/components/card/card-detail-edit.component";
import { Def } from "@card/common/definitions";
import { PaymentSchedule } from "@app/models/PaymentSchedule";
import { UserStore } from "@app/services/stores/UserStore";
import { DetailsPropagateDefaultDialog } from "@app/pages/wallet/dialogs/details-propagate-default.dialog";
import { MatDialog } from "@angular/material/dialog";
import { PaymentMethod } from "@app/models/PaymentMethod";
import { DialogErrorDialog } from "@app/components/sharedDialogs/dialogError.dialog";
import { NetworkErrorService } from "@app/services/NetworkError.service";
import { ErrorHandlerService } from "@app/services/ErrorHandler.service";
import { CardDetailsDeleteDialog } from "@app/pages/wallet/editcarddetails/cardDetailsDelete.dialog";

@Component({
    selector: 'app-credit-card-checkout-edit',
    template: require('./credit-card-edit.component.html')
})
export class CreditCardCheckoutEditComponent implements OnInit{

    @Input() private paymentMethodId: number;

    @Output() private return: EventEmitter<void> = new EventEmitter<void>()
    @Output() private goto: EventEmitter<void> = new EventEmitter<void>()

    @ViewChild(CardAddressComponent) private cardAddressComponent: CardAddressComponent;
    @ViewChild(CardDetailEditComponent) private cardDetailEditComponent: CardDetailEditComponent;

    private fullySetUp = false;
    private UNSELECTED = -1
    private addressFormValid: boolean = false;
    private cardDetailEditProps: CardDetailEditProps;
    private paymentMethodVersion: number;
    private paymentSchedules: PaymentSchedule[];
    private prefillAddressForm = this.UNSELECTED;
    private cardFormValid: boolean = false;
    private cardDetailEditData: CardDetailEditData;
    private addressFormData: AddressFormData;

    addressProps: AddressFormComponentProps = {
        disablePOBoxes: false
    }

    constructor(@Inject(SpinnerService) private spinnerService: SpinnerService,
                @Inject(PaymentMethodApi) private paymentMethodApi: PaymentMethodApi,
                @Inject(UserStore) private userStore: UserStore,
                @Inject(MatDialog) private dialog: MatDialog,
                @Inject(NetworkErrorService) private networkErrorService: NetworkErrorService,
                @Inject(ErrorHandlerService) private errorHandlerService: ErrorHandlerService) {
    }

    ngOnInit() {

        this.cardDetailEditProps = {
            disableClearCard: false,

            /** disable changing the expiration date */
            disableExpirationDate : false,
            /** disable changing the name on the card */
            disableNameOnCard: false,
            /** mark as default payment method */
            makeDefault: false,

            existingCard: null,
            addressDisabled: false,
            cardDisabled: false,
            nickName: "",

        }

        this.setUp()
    }

    onAddressValid(isValid: boolean) {
        this.addressFormValid = isValid;
    }

    onCancel() {
        this.return.emit();
    }

    preSave() {

        this.cardDetailEditData = this.cardDetailEditComponent.getFormData();
        this.addressFormData = this.cardAddressComponent.getFormData();

        if (this.cardDetailEditData.makeDefault && this.paymentSchedules && this.paymentSchedules.length > 0) {
            let dialogRef = this.dialog.open(DetailsPropagateDefaultDialog, {
                data: { paymentSchedules: this.paymentSchedules, type: 'Save' }
            });
            dialogRef.afterClosed().pipe(take(1)).subscribe(value => {
                if (value === 'Save') {
                    this.onSave(false);
                } else if (value === 'Update') {
                    this.onSave(true);
                }
            });
        } else {
            this.onSave(false);
        }
    }

    onSave(propagatePmtMethod: boolean) {

        if (!this.addressFormValid || !this.cardFormValid) return
        this.spinnerService.showSpinner()

        this.paymentMethodApi.paymentMethodsIdAllowedUpdateGet(''+this.paymentMethodId)
            .pipe(take(1)).subscribe(() => {
                const userId = this.userStore.getHoaHomeownerId()
                this.cardDetailEditComponent.updateCard(
                    userId,
                    this.cardDetailEditProps.existingCard.paymentToken,
                    this.cardDetailEditProps.existingCard.maskedCardNumber,
                    this.cardDetailEditProps.existingCard.cardBrand,
                    this.formatAddressForUpdate(this.addressFormData)).then(cardUpdate => {
                    const paymentMethod: PaymentMethod = {
                        id: this.paymentMethodId,
                        payerId: userId,
                        token: String(cardUpdate.paymentToken),
                        cardType: "DEBIT", // todo: figure this out
                        // ^-- Chris B said not an issue "for now" - we cant determine card type anyways apparently
                        cardBrand: (cardUpdate.cardBrand || '').toLowerCase() || undefined, // 'amex', 'discover', 'mastercard', 'visa'
                        maskedId: cardUpdate.maskedCardNumber.slice(-4),
                        cardHolderName: this.cardDetailEditData.nameOnCard,
                        nickname: this.cardDetailEditData.nickName,
                        defaultMethod: this.cardDetailEditData.makeDefault,
                        expirationYear: this.cardDetailEditData.expYear,
                        expirationMonth: this.cardDetailEditData.expMonth,
                        line1: this.addressFormData.address1,
                        line2: this.addressFormData.address2,
                        unit: this.addressFormData.unit,
                        city: this.addressFormData.city,
                        state: this.addressFormData.state,
                        zipcode: this.addressFormData.zip,
                        country: this.addressFormData.country,
                        version: this.paymentMethodVersion,
                    }
                    this.paymentMethodApi.paymentMethodsPost(paymentMethod)
                        .pipe(take(1)).subscribe(async (resp) => {
                            if (propagatePmtMethod && this.paymentSchedules && this.paymentSchedules.length) {
                                await this.paymentMethodApi.paymentMethodPropagateToSchedules(this.paymentSchedules, resp,
                                    this.paymentSchedules[0].paymentMethod.type !== 'card').then(resp => {
                                    if (resp && resp.length > 0) {
                                        this.dialog.open(DialogErrorDialog,{
                                            data: {
                                                message: 'An error occurred when attempting to save or update Payment Schedules. ' +
                                                    'Please verify that the schedules: ' + resp.join(',') + ' were processed correctly. ' +
                                                    'If not, you may need to change the payment method manually.'
                                            }
                                        });
                                    }
                                },
                                () => {
                                    this.dialog.open(DialogErrorDialog, {
                                        data: { error: 'An error occurred when attempting to save or update Payment Schedules. ' +
                                                'Please verify that the schedules were processed correctly. If not, you may ' +
                                                'need to change the payment method manually.' }
                                    });
                                });
                            }
                            this.spinnerService.closeSpinner();
                            this.goto.emit();

                        }, (err) => {
                            this.networkErrorService.handleNetworkError(err, true)
                                .pipe(take(1)).subscribe((resp) => {
                                    this.spinnerService.closeSpinner()
                                    if (resp === true) {
                                        this.onSave(propagatePmtMethod);
                                    }
                                });
                        });
                }).catch(() => {
                    let errMsg: string = 'System error occurred. Please try again in a few hours.';
                    let err = {
                        status: 400
                    }
                    this.errorHandlerService.handleNetworkError(err, true, errMsg)
                        .pipe(take(1)).subscribe();
                });
            }, (err) => {
                this.spinnerService.closeSpinner();
                let errMsg: string = undefined;
                if (err.error && err.error.message) {
                    errMsg = err.error.message;
                }
                this.errorHandlerService.handleNetworkError(err, true, errMsg)
                    .pipe(take(1)).subscribe();
            });
    }

    private formatAddressForUpdate(addressFormData: AddressFormData): Def.BillingAddress {
        return {
            line1: addressFormData.address1,
            line2: addressFormData.address2,
            unit: addressFormData.unit,
            city: addressFormData.city,
            state: addressFormData.state,
            zipcode: addressFormData.zip,
            country: addressFormData.country,
        }
    }

    removeCard() {
        const dialogRef = this.dialog.open(CardDetailsDeleteDialog);
        dialogRef.afterClosed().pipe(take(1)).subscribe(value => {
            if (value === 'Delete') {
                this.paymentMethodApi.paymentMethodsIdDelete(this.paymentMethodId)
                    .pipe(take(1)).subscribe(() => {
                        this.goto.emit();
                    }, (err) => {
                        console.error(err);
                        this.networkErrorService.handleNetworkError(err, true)
                            .pipe(take(1)).subscribe((resp) => {
                                if (resp === true) {
                                    this.removeCard();
                                }
                            });
                    })
            }
        });
    }


    private setUp() {
        this.paymentMethodApi.paymentMethodsIdGet(this.paymentMethodId)
            .pipe(take(1)).subscribe(data => {

                if (data == null) throw new Error("Error: Payment Method Data cannot be null!");

                this.paymentMethodVersion = data.version

                const billingAddress: Def.BillingAddress = {
                    line1: data.line1,
                    line2: data.line2,
                    unit: data.unit,
                    state: data.state,
                    city: data.city,
                    zipcode: data.zipcode,
                    country: data.country,
                }

                this.cardAddressComponent.setFormData({
                    address1: data.line1,
                    address2: data.line2,
                    unit: data.unit,
                    state: data.state,
                    city: data.city,
                    zip: data.zipcode,
                    country: data.country,
                })

                this.addressFormValid = true;

                this.cardDetailEditProps.existingCard = {
                    billingAddress: billingAddress,
                    cardBrand: data.cardBrand,
                    cardHolderName: data.cardHolderName,
                    expirationMonth: data.expirationMonth,
                    expirationYear: data.expirationYear,
                    maskedCardNumber: data.maskedId,
                    paymentToken: Number(data.token),
                    securityCode: '', // current security code is something that must be filled in
                    customerId: data.payerId,
                }
                this.cardFormValid = false;

                if (!data.defaultMethod) {
                    this.paymentMethodApi.getDefaultPaymentMethodSchedules(this.userStore.getHoaHomeownerId())
                        .pipe(take(1)).subscribe(resp => {
                            this.paymentSchedules = resp;
                        });
                }

                this.cardDetailEditProps.makeDefault = data.defaultMethod;
                this.cardDetailEditProps.nickName = data.nickname;
                this.spinnerService.closeSpinner();
                this.fullySetUp = true;

            });
    }

    onCardValid(isValid: boolean) {
        this.cardFormValid = isValid;
    }

}
